import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, useTheme, IconButton, Collapse, LinearProgress
} from '@mui/material';
import {
  KeyboardArrowDown, KeyboardArrowUp, Star, TrendingUp,
  Warning, CheckCircle, Error, Info
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import AdvancedCapabilitiesAnalysis from '../utils/advancedCapabilitiesAnalysis';

const AdvancedCapabilitiesMatrix = ({ scenarios, categories, industry, tier }) => {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedScenario, setSelectedScenario] = useState(null);

  const analysis = useMemo(() => {
    return AdvancedCapabilitiesAnalysis.analyzeScenarioCapabilities(
      scenarios, categories, industry, tier
    );
  }, [scenarios, categories, industry, tier]);

  const toggleRowExpand = (capabilityKey) => {
    setExpandedRows(prev => ({
      ...prev,
      [capabilityKey]: !prev[capabilityKey]
    }));
  };

  const CapabilitiesComparisonTable = () => (
    <TableContainer component={Paper} sx={{ mt: 3 }}>
      <Table sx={{ minWidth: 1000 }} size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'primary.main' }}>
            <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '250px' }}>
              Capability
            </TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '150px' }}>
              Category
            </TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '120px' }}>
              Type
            </TableCell>
            {scenarios.map((scenario, index) => (
              <TableCell key={index} align="center" sx={{ color: 'white', fontWeight: 'bold', minWidth: '200px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Chip 
                    label={`S${index + 1}`} 
                    size="small" 
                    sx={{ 
                      bgcolor: selectedScenario === index ? 'secondary.main' : 'rgba(255,255,255,0.2)',
                      color: 'white',
                      mb: 0.5
                    }}
                    onClick={() => setSelectedScenario(selectedScenario === index ? null : index)}
                  />
                  <Typography variant="caption" sx={{ color: 'white', maxWidth: '180px', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {scenario.name || `Scenario ${index + 1}`}
                  </Typography>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {analysis.capabilitiesMatrix.capabilities.map((capability, capIndex) => (
            <React.Fragment key={capability}>
              <TableRow 
                sx={{ 
                  bgcolor: capIndex % 2 === 0 ? 'action.hover' : 'background.paper',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
                onClick={() => toggleRowExpand(capability)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton size="small">
                      {expandedRows[capability] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                    <Typography variant="body2" fontWeight="medium">
                      {capability}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={getCapabilityCategory(capability, analysis)} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={getCapabilityType(capability, analysis)} 
                    size="small" 
                    color={getCapabilityTypeColor(getCapabilityType(capability, analysis))}
                  />
                </TableCell>
                {scenarios.map((_, scenarioIndex) => {
                  const scenarioCap = analysis.capabilitiesMatrix.data[scenarioIndex][capability];
                  return (
                    <TableCell key={scenarioIndex} align="center">
                      {scenarioCap.present ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Chip 
                            label={`L${scenarioCap.level}`}
                            size="small"
                            color={getLevelColor(scenarioCap.level)}
                            variant="filled"
                          />
                          {scenarioCap.score > 0 && (
                            <LinearProgress 
                              variant="determinate" 
                              value={scenarioCap.score} 
                              sx={{ 
                                width: '60px', 
                                height: '6px',
                                borderRadius: '3px',
                                bgcolor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: getScoreColor(scenarioCap.score)
                                }
                              }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Chip 
                          label="Not Available" 
                          size="small" 
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
              
              {/* Expanded row with detailed information */}
              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3 + scenarios.length}>
                  <Collapse in={expandedRows[capability]} timeout="auto" unmountOnExit>
                    <Box sx={{ margin: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Capability Details: {capability}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Scenario Availability
                          </Typography>
                          {scenarios.map((scenario, index) => {
                            const scenarioCap = analysis.capabilitiesMatrix.data[index][capability];
                            return (
                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CheckCircle 
                                  sx={{ 
                                    fontSize: 16, 
                                    mr: 1, 
                                    color: scenarioCap.present ? 'success.main' : 'grey.400' 
                                  }} 
                                />
                                <Typography variant="body2">
                                  Scenario {index + 1}: {scenarioCap.present ? 
                                    `Level ${scenarioCap.level} (Score: ${scenarioCap.score})` : 
                                    'Not Available'
                                  }
                                </Typography>
                              </Box>
                            );
                          })}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Strategic Value
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TrendingUp sx={{ fontSize: 16, mr: 1, color: 'info.main' }} />
                            <Typography variant="body2">
                              Business Impact: {estimateBusinessImpact(capability)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Warning sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
                            <Typography variant="body2">
                              Implementation Complexity: {estimateComplexity(capability)}/10
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const SummaryCards = () => (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} md={3}>
        <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <CardContent>
            <Typography variant="h4" fontWeight="bold">
              {analysis.capabilitiesMatrix.summary.totalCapabilities}
            </Typography>
            <Typography variant="body2">
              Unique Capabilities
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={9}>
        <Grid container spacing={2}>
          {scenarios.map((scenario, index) => {
            const coverage = analysis.capabilitiesMatrix.summary.scenarioCoverage[scenario.name || `Scenario ${index + 1}`];
            return (
              <Grid item xs={12} md={4} key={index}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary">
                      {coverage?.count || 0}
                    </Typography>
                    <Typography variant="body2">
                      Capabilities in {scenario.name || `S${index + 1}`}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={coverage?.coverage || 0} 
                      sx={{ mt: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(coverage?.coverage || 0)}% coverage
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
        Advanced Capabilities Matrix
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Comprehensive analysis of capabilities across selected scenarios based on slider level configurations
      </Typography>

      <SummaryCards />
      <CapabilitiesComparisonTable />

      {/* Gap Analysis Section */}
      {analysis.gapAnalysis.capabilityGaps.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h5" gutterBottom>
            Capability Gap Analysis
          </Typography>
          <Grid container spacing={2}>
            {analysis.gapAnalysis.capabilityGaps.slice(0, 6).map((gap, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {gap.capability}
                      </Typography>
                      <Chip label={gap.scenario} size="small" color="primary" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {gap.category} • Level {gap.level}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Chip label={`Impact: $${(gap.businessImpact/1000).toFixed(0)}K`} size="small" />
                      <Chip label={`Effort: ${gap.implementationEffort}/10`} size="small" color="secondary" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

// Helper functions
const getCapabilityCategory = (capability, analysis) => {
  // Logic to determine category based on capability name
  if (capability.includes('AI') || capability.includes('Analytics')) return 'Advanced Analytics';
  if (capability.includes('Collaboration') || capability.includes('Integration')) return 'Collaboration';
  if (capability.includes('Automation') || capability.includes('Efficiency')) return 'Automation';
  return 'General';
};

const getCapabilityType = (capability, analysis) => {
  if (capability.includes('PLM') || capability.includes('Lifecycle')) return 'PLM';
  if (capability.includes('Operational') || capability.includes('Efficiency')) return 'Operational';
  if (capability.includes('Strategic') || capability.includes('Innovation')) return 'Strategic';
  return 'Functional';
};

const getCapabilityTypeColor = (type) => {
  const colors = {
    PLM: 'primary',
    Operational: 'success',
    Strategic: 'warning',
    Functional: 'info'
  };
  return colors[type] || 'default';
};

const getLevelColor = (level) => {
  if (level >= 8) return 'success';
  if (level >= 5) return 'warning';
  return 'error';
};

const getScoreColor = (score) => {
  if (score >= 80) return 'success.main';
  if (score >= 60) return 'warning.main';
  return 'error.main';
};

const estimateBusinessImpact = (capability) => {
  // Simplified impact estimation
  if (capability.includes('AI') || capability.includes('Predictive')) return 'High';
  if (capability.includes('Automation') || capability.includes('Optimization')) return 'Medium-High';
  return 'Medium';
};

const estimateComplexity = (capability) => {
  // Simplified complexity estimation
  if (capability.includes('AI') || capability.includes('Integration')) return 8;
  if (capability.includes('Automation') || capability.includes('Analytics')) return 5;
  return 3;
};

export default AdvancedCapabilitiesMatrix;